// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { unitGlyph } from '../rules/ui';
import {
  type Building,
  buildingMaxHp,
  type Command,
  canAttack,
  canHit,
  type EntityId,
  fromMilli,
  type PlayerId,
  type SimState,
  type Unit,
  unitHealRate,
  unitMaxHp,
} from '../sim';
import { distanceTo } from '../sim/footprint';
import { firingFor, hitChance } from '../sim/systems/combat';
import { unitStrength } from '../sim/veterancy';
import { buildingIcon } from './buildingsList';
import { focusTargets } from './focus';

/**
 * The selection's focus queue as the map markers, the queue panel and the
 * hover tooltip show it (pure, no React): the targets in order, who is on
 * each, and the damage the selected units are expected to deal to a target
 * (mirroring the sim's `combat.ts`, so the numbers match what happens).
 */

/** One target of the selection's focus: #1.. are current, the rest queued. */
export interface FocusEntry {
  targetId: EntityId;
  /** 1-based position on the map markers and in the panel. */
  index: number;
  /** Some selected unit is attacking it now (else it is only queued). */
  current: boolean;
  /** Selected own units going for it now or queued to, in selection order. */
  unitIds: EntityId[];
}

/**
 * The focus targets of the selected own units. Current targets come first (in
 * `focusTargets` order), then queued ones: units in selection order, each
 * unit's queue in order, a target once. Targets gone from `state` are skipped.
 */
export function focusEntries(
  state: SimState,
  selectedUnitIds: readonly EntityId[],
  playerId: PlayerId
): FocusEntry[] {
  const exists = (id: EntityId) => !!state.units[id] || !!state.buildings[id];
  const byId = new Map<EntityId, FocusEntry>();
  const entries: FocusEntry[] = [];
  const add = (targetId: EntityId, unitId: EntityId, current: boolean) => {
    let entry = byId.get(targetId);
    if (!entry) {
      entry = { targetId, index: entries.length + 1, current, unitIds: [] };
      byId.set(targetId, entry);
      entries.push(entry);
    }
    if (!entry.unitIds.includes(unitId)) entry.unitIds.push(unitId);
  };
  for (const target of focusTargets(state, selectedUnitIds, playerId)) {
    for (const id of target.attackerIds) add(target.targetId, id, true);
  }
  for (const id of selectedUnitIds) {
    const unit = state.units[id];
    if (!unit || unit.owner !== playerId || unit.order?.kind !== 'attack') continue;
    for (const queued of unit.order.queue ?? []) {
      if (exists(queued)) add(queued, id, false);
    }
  }
  return entries;
}

/** The expected damage of one unit on a target, per second. */
export interface UnitDamage {
  dps: number;
  /** Hit chance of a ranged unit's shots (1 = always), null for melee. */
  chance: number | null;
}

/** `home`: the unit stands in its owner's territory as the player knows it (`atHome`). */
type Target =
  | { kind: 'unit'; unit: Unit; home?: boolean }
  | { kind: 'building'; building: Building };

/**
 * Whether rival `unit` stands in its owner's territory, judged from the
 * territories the player knows (`knownTerritories`, fog-safe), not hidden state.
 */
export function atHome(
  territories: ReadonlyMap<PlayerId, ReadonlySet<HexTileMapKey>>,
  unit: Unit
): boolean {
  return territories.get(unit.owner)?.has(getHexTileMapKey(unit.pos)) ?? false;
}

/**
 * What `unit` is expected to deal per second to `target`, or null when it
 * can't attack it. Mirrors `combat.ts`: strength (rank included) × the
 * attacker type's `bonusVs` / `bonusVsBuildings`, × (1 − `pacing.homeDefence`)
 * for a unit target at home (`Target.home`), and for a ranged unit its hit
 * chance at the distance (clamped to its range: it stands at most that far;
 * a target on the move is hit less, ignored here). Strength is the dps at
 * 100% accuracy, so the fire interval cancels.
 */
export function unitDamage(rules: RuleSet, unit: Unit, target: Target): UnitDamage | null {
  const def = rules.units[unit.type];
  if (!def || !canAttack(rules, unit)) return null;
  const isUnit = target.kind === 'unit';
  if (!canHit(rules, unit.type, isUnit)) return null;
  const bonus = isUnit ? (def.bonusVs?.[target.unit.type] ?? 1) : (def.bonusVsBuildings ?? 1);
  const home = isUnit && target.home ? 1 - rules.pacing.homeDefence : 1;
  const dps = unitStrength(rules, unit) * bonus * home;
  const firing = firingFor(rules, unit);
  if (!firing) return { dps, chance: null };
  const entity = isUnit ? target.unit : target.building;
  const distance = Math.min(Math.max(1, distanceTo(entity, unit.pos)), def.range);
  const chance = hitChance(firing.accuracy, distance);
  return { dps: dps * chance, chance };
}

/** The selected units of one type, and what they deal to a target together. */
export interface DamageGroup {
  type: string;
  glyph: string;
  count: number;
  /** Average per unit, per second. */
  each: number;
  total: number;
  /** Average hit chance of the ranged ones, null for melee. */
  chance: number | null;
}

export interface DamageReport {
  groups: DamageGroup[];
  total: number;
}

/** Damage of `unitIds` (own units that can hit it) on `target`, grouped by type. */
export function damageReport(
  rules: RuleSet,
  state: SimState,
  unitIds: readonly EntityId[],
  target: Target
): DamageReport {
  const byType = new Map<string, DamageGroup & { chances: number }>();
  let total = 0;
  for (const id of unitIds) {
    const unit = state.units[id];
    if (!unit) continue;
    const damage = unitDamage(rules, unit, target);
    if (!damage) continue;
    let group = byType.get(unit.type);
    if (!group) {
      group = {
        type: unit.type,
        glyph: unitGlyph(rules, unit.type),
        count: 0,
        each: 0,
        total: 0,
        chance: null,
        chances: 0,
      };
      byType.set(unit.type, group);
    }
    group.count += 1;
    group.total += damage.dps;
    if (damage.chance !== null) {
      group.chances += damage.chance;
      group.chance = 0;
    }
    total += damage.dps;
  }
  const groups = [...byType.values()].map(({ chances, ...g }) => ({
    ...g,
    each: g.total / g.count,
    chance: g.chance === null ? null : chances / g.count,
  }));
  return { groups, total };
}

/** Seconds to deal `hp` at `dps`; null when nothing is dealt. */
export function timeToKill(hp: number, dps: number): number | null {
  return dps > 0 ? hp / dps : null;
}

/** "1.1s" under ten seconds, "14s" up to a minute, then "1m 05s". */
export function durationText(seconds: number): string {
  if (seconds < 10) return `${Number(seconds.toFixed(1))}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

/** Damage per second as written on the cards: "16/s", "8.3/s". */
export function dpsText(dps: number): string {
  return `${Number(dps.toFixed(1))}/s`;
}

/** "Wr ×4 · Ar ×2 · 21/s": who is on a target and what they deal. */
export function assignedText(report: DamageReport): string {
  if (report.groups.length === 0) return 'none can hit it';
  const who = report.groups.map((g) => `${g.glyph} ×${g.count}`).join(' ');
  return `${who} · ${dpsText(report.total)}`;
}

/** A row of the focus queue panel. */
export interface FocusRow {
  targetId: EntityId;
  index: number;
  current: boolean;
  glyph: string;
  name: string;
  /** Whose it is; null for a target out of sight. */
  owner: PlayerId | null;
  /** False: out of sight, so hp and time to kill are withheld (fog). */
  seen: boolean;
  hp: number;
  maxHp: number;
  report: DamageReport;
  /** Seconds the assigned units need, null when they can't or it's unseen. */
  ttk: number | null;
}

/**
 * The panel rows for `entries`. `isSeen` says whether a rival's target is in
 * sight (fog); an unseen one gets no name or numbers, only its place.
 */
export function focusRows(
  rules: RuleSet,
  state: SimState,
  entries: readonly FocusEntry[],
  playerId: PlayerId,
  isSeen: (target: Unit | Building) => boolean,
  isHome: (unit: Unit) => boolean = () => false
): FocusRow[] {
  const rows: FocusRow[] = [];
  for (const entry of entries) {
    const unit = state.units[entry.targetId];
    const building = state.buildings[entry.targetId];
    const subject = unit ?? building;
    if (!subject) continue;
    const base = { targetId: entry.targetId, index: entry.index, current: entry.current };
    if (subject.owner !== playerId && !isSeen(subject)) {
      rows.push({
        ...base,
        glyph: '?',
        name: 'Out of sight',
        owner: null,
        seen: false,
        hp: 0,
        maxHp: 0,
        report: { groups: [], total: 0 },
        ttk: null,
      });
      continue;
    }
    const target: Target = unit
      ? { kind: 'unit', unit, home: isHome(unit) }
      : { kind: 'building', building: building as Building };
    const report = damageReport(rules, state, entry.unitIds, target);
    const hp = fromMilli(subject.hp);
    rows.push({
      ...base,
      glyph: unit ? unitGlyph(rules, unit.type) : buildingIcon((building as Building).type),
      name: unit
        ? (rules.units[unit.type]?.name ?? unit.type)
        : (rules.buildings[(building as Building).type]?.name ?? (building as Building).type),
      owner: subject.owner,
      seen: true,
      hp,
      maxHp: fromMilli(
        unit ? unitMaxHp(rules, unit) : buildingMaxHp(state, rules, building as Building)
      ),
      report,
      ttk: timeToKill(hp, report.total),
    });
  }
  return rows;
}

/** Selected own units on an attack order that has `targetId` current or queued (all when omitted). */
function focusUnits(
  state: SimState,
  selected: readonly EntityId[],
  playerId: PlayerId,
  targetId?: EntityId
): EntityId[] {
  return selected.filter((id) => {
    const order = state.units[id]?.order;
    if (state.units[id]?.owner !== playerId || order?.kind !== 'attack') return false;
    return (
      targetId === undefined || order.targetId === targetId || !!order.queue?.includes(targetId)
    );
  });
}

/** `unfocus` for one target of the queue (current: the unit moves to its next), or null if none are on it. */
export function unfocusTargetCommand(
  state: SimState,
  selected: readonly EntityId[],
  playerId: PlayerId,
  targetId: EntityId
): Command | null {
  const unitIds = focusUnits(state, selected, playerId, targetId);
  return unitIds.length > 0 ? { type: 'unfocus', unitIds, targetId } : null;
}

/** `unfocus` with no target: drops every selected unit's queue and attack order. */
export function clearFocusCommand(
  state: SimState,
  selected: readonly EntityId[],
  playerId: PlayerId
): Command | null {
  const unitIds = focusUnits(state, selected, playerId);
  return unitIds.length > 0 ? { type: 'unfocus', unitIds } : null;
}

/** Backspace: `unfocus` of the last queued (not current) target, or null when nothing is queued. */
export function unfocusLastCommand(
  state: SimState,
  selected: readonly EntityId[],
  playerId: PlayerId
): Command | null {
  const queued = focusEntries(state, selected, playerId).filter((e) => !e.current);
  const last = queued[queued.length - 1];
  return last ? unfocusTargetCommand(state, selected, playerId, last.targetId) : null;
}

/** What the hover tooltip tells of a rival unit in sight. */
export interface EnemyTip {
  unitId: EntityId;
  glyph: string;
  name: string;
  owner: PlayerId;
  hp: number;
  maxHp: number;
  /** Its attack: dps (rank included) and range; null if it doesn't attack. */
  attack: { dps: number; range: number } | null;
  /** Its healing: hp/s and range; null if it doesn't heal. */
  heals: { rate: number; range: number } | null;
  /** The selected units' expected damage on it. */
  report: DamageReport;
  ttk: number | null;
}

/** The tooltip for rival `unit` against the selected units (caller checks it is in sight). */
export function enemyTip(
  rules: RuleSet,
  state: SimState,
  unit: Unit,
  selected: readonly EntityId[],
  playerId: PlayerId,
  home = false
): EnemyTip {
  const def = rules.units[unit.type];
  const mine = selected.filter((id) => state.units[id]?.owner === playerId);
  const report = damageReport(rules, state, mine, { kind: 'unit', unit, home });
  const hp = fromMilli(unit.hp);
  const attacks = !!def && def.actions.includes('attack') && def.strength > 0;
  const healRate = unitHealRate(rules, unit);
  return {
    unitId: unit.id,
    glyph: unitGlyph(rules, unit.type),
    name: def?.name ?? unit.type,
    owner: unit.owner,
    hp,
    maxHp: fromMilli(unitMaxHp(rules, unit)),
    attack: attacks ? { dps: unitStrength(rules, unit), range: def.range } : null,
    heals: healRate > 0 ? { rate: healRate, range: def?.range ?? 1 } : null,
    report,
    ttk: timeToKill(hp, report.total),
  };
}
